import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LICENSE_OPTIONS = [
  "Life Insurance",
  "Health Insurance",
  "Medicare",
  "Property & Casualty",
  "Securities (Series 6 / 7 / 63 / 65 / 66)",
  "None",
];
const DURATION_OPTIONS = ["Not Licensed", "Less than 1 year", "1–3 years", "3–5 years", "5+ years"];
const SITUATION_OPTIONS = [
  "I'm already a licensed agent looking for a better opportunity.",
  "I'm licensed but not currently active.",
  "I'm working on getting licensed.",
  "I'm exploring the insurance industry as a new career.",
];

const labelCls = "mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mb-3 block";
const inputCls =
  "w-full bg-[#111] border rounded-sm px-4 py-3.5 text-[#f4f0ea] text-base placeholder-[#6a6a6a] focus:outline-none focus:ring-1 focus:ring-[#d4af37]/30 transition-colors duration-300";

const Field = ({ label, required, error, children }) => (
  <div>
    <label className={labelCls}>
      {label}
      {required && <span className="text-[#d4af37] ml-1">*</span>}
    </label>
    {children}
    {error && <p className="mono text-[10px] tracking-wide text-[#e0a4a4] mt-2">{error}</p>}
  </div>
);

const OptionRow = ({ selected, onClick, type, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full flex items-center gap-3 px-4 py-3 border rounded-sm text-left transition-colors duration-300 ${
      selected
        ? "border-[#d4af37] bg-[#d4af37]/[0.06]"
        : "border-white/10 hover:border-[#d4af37]/40"
    }`}
  >
    <span
      className={`shrink-0 h-5 w-5 flex items-center justify-center border transition-colors duration-300 ${
        type === "radio" ? "rounded-full" : "rounded-[3px]"
      } ${selected ? "border-[#d4af37] bg-[#d4af37]" : "border-white/25"}`}
    >
      {selected &&
        (type === "radio" ? (
          <span className="h-2 w-2 rounded-full bg-[#030303]" />
        ) : (
          <Check size={13} className="text-[#030303]" strokeWidth={3} />
        ))}
    </span>
    <span className={`text-sm leading-snug ${selected ? "text-[#f4f0ea]" : "text-[#c9c4bd]"}`}>{children}</span>
  </button>
);

const initialState = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  licensed_life: "",
  licenses: [],
  licensed_duration: "",
  submitted_before: "",
  current_affiliation: "",
  current_situation: "",
  company_website: "", // honeypot
};

export const RecruitingForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggleLicense = (opt) => {
    setForm((f) => {
      const has = f.licenses.includes(opt);
      let next;
      if (opt === "None") next = has ? [] : ["None"];
      else {
        next = has ? f.licenses.filter((l) => l !== opt) : [...f.licenses.filter((l) => l !== "None"), opt];
      }
      return { ...f, licenses: next };
    });
    setErrors((e) => ({ ...e, licenses: undefined }));
  };

  const validate = () => {
    const e = {};
    const req = ["first_name", "last_name", "phone", "email", "city", "state"];
    req.forEach((k) => {
      if (!form[k].trim()) e[k] = "Required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.phone && form.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone number";
    if (!form.licensed_life) e.licensed_life = "Please select one";
    if (form.licenses.length === 0) e.licenses = "Select at least one";
    if (!form.licensed_duration) e.licensed_duration = "Please select one";
    if (!form.submitted_before) e.submitted_before = "Please select one";
    if (!form.current_situation) e.current_situation = "Please select one";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      const first = document.querySelector(`[data-error="true"]`);
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/recruiting-applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submission failed");
      }
      navigate("/join/application-received");
    } catch (err) {
      setSubmitting(false);
      toast.error(err.message || "Something went wrong. Please try again.");
    }
  };

  const errBorder = (k) => (errors[k] ? "border-[#e0a4a4]/60" : "border-white/10 focus:border-[#d4af37]/60");

  return (
    <form onSubmit={handleSubmit} data-testid="recruiting-form" noValidate className="space-y-14">
      {/* Honeypot (hidden from users) */}
      <input
        type="text"
        name="company_website"
        value={form.company_website}
        onChange={(e) => set("company_website", e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      {/* Contact Information */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Contact Information</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="First Name" required error={errors.first_name}>
            <input data-error={!!errors.first_name} data-testid="rf-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={`${inputCls} ${errBorder("first_name")}`} placeholder="Jane" />
          </Field>
          <Field label="Last Name" required error={errors.last_name}>
            <input data-error={!!errors.last_name} data-testid="rf-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={`${inputCls} ${errBorder("last_name")}`} placeholder="Doe" />
          </Field>
          <Field label="Phone Number" required error={errors.phone}>
            <input data-error={!!errors.phone} data-testid="rf-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={`${inputCls} ${errBorder("phone")}`} placeholder="(623) 555-0100" />
          </Field>
          <Field label="Email Address" required error={errors.email}>
            <input data-error={!!errors.email} data-testid="rf-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`${inputCls} ${errBorder("email")}`} placeholder="you@email.com" />
          </Field>
          <Field label="City" required error={errors.city}>
            <input data-error={!!errors.city} data-testid="rf-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={`${inputCls} ${errBorder("city")}`} placeholder="Phoenix" />
          </Field>
          <Field label="State" required error={errors.state}>
            <input data-error={!!errors.state} data-testid="rf-state" value={form.state} onChange={(e) => set("state", e.target.value)} className={`${inputCls} ${errBorder("state")}`} placeholder="Arizona" />
          </Field>
        </div>
      </div>

      {/* Licensing */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Licensing</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="space-y-10">
          <Field label="Are you currently licensed to sell Life Insurance?" required error={errors.licensed_life}>
            <div data-error={!!errors.licensed_life} className="grid grid-cols-2 gap-4 max-w-md">
              {["Yes", "No"].map((o) => (
                <OptionRow key={o} type="radio" testid={`rf-licensed-${o.toLowerCase()}`} selected={form.licensed_life === o} onClick={() => set("licensed_life", o)}>{o}</OptionRow>
              ))}
            </div>
          </Field>

          <Field label="Which license(s) do you currently hold?" required error={errors.licenses}>
            <div data-error={!!errors.licenses} className="grid sm:grid-cols-2 gap-4">
              {LICENSE_OPTIONS.map((o) => (
                <OptionRow key={o} type="checkbox" testid={`rf-license-${o.slice(0, 6).toLowerCase().replace(/\W/g, "")}`} selected={form.licenses.includes(o)} onClick={() => toggleLicense(o)}>{o}</OptionRow>
              ))}
            </div>
          </Field>

          <Field label="How long have you been licensed?" required error={errors.licensed_duration}>
            <div data-error={!!errors.licensed_duration} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {DURATION_OPTIONS.map((o) => (
                <OptionRow key={o} type="radio" testid={`rf-duration-${o.slice(0, 4).toLowerCase().replace(/\W/g, "")}`} selected={form.licensed_duration === o} onClick={() => set("licensed_duration", o)}>{o}</OptionRow>
              ))}
            </div>
          </Field>
        </div>
      </div>

      {/* Experience */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Experience</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="space-y-10">
          <Field label="Have you ever submitted life insurance applications before?" required error={errors.submitted_before}>
            <div data-error={!!errors.submitted_before} className="grid grid-cols-2 gap-4 max-w-md">
              {["Yes", "No"].map((o) => (
                <OptionRow key={o} type="radio" testid={`rf-submitted-${o.toLowerCase()}`} selected={form.submitted_before === o} onClick={() => set("submitted_before", o)}>{o}</OptionRow>
              ))}
            </div>
          </Field>

          <Field label="If yes, who are you currently affiliated with?">
            <input data-testid="rf-affiliation" value={form.current_affiliation} onChange={(e) => set("current_affiliation", e.target.value)} className={`${inputCls} border-white/10 focus:border-[#d4af37]/60`} placeholder="Independent • Captive • Brokerage • None" />
          </Field>
        </div>
      </div>

      {/* Current Situation */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Current Situation</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <Field label="What best describes your current situation?" required error={errors.current_situation}>
          <div data-error={!!errors.current_situation} className="grid gap-4">
            {SITUATION_OPTIONS.map((o, i) => (
              <OptionRow key={o} type="radio" testid={`rf-situation-${i}`} selected={form.current_situation === o} onClick={() => set("current_situation", o)}>{o}</OptionRow>
            ))}
          </div>
        </Field>
      </div>

      {/* Submit */}
      <div className="pt-2">
        <motion.button
          type="submit"
          data-testid="rf-submit"
          disabled={submitting}
          whileTap={{ scale: 0.98 }}
          className="group inline-flex items-center justify-center gap-3 mono text-xs uppercase tracking-[0.2em] px-10 py-4 bg-[#d4af37] text-[#030303] rounded-full hover:bg-[#f4f0ea] transition-colors duration-400 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Submitting…
            </>
          ) : (
            <>Submit Application</>
          )}
        </motion.button>
        <p className="text-[#6a6a6a] text-xs leading-relaxed tracking-wide mt-5 max-w-xl">
          By submitting, you consent to be contacted by John Campbell Insurance regarding this opportunity. Your
          information is never sold or shared.
        </p>
      </div>
    </form>
  );
};
